import { Api } from "@/services/api/api";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { apiConfig } from "@/services/api/api.config";
import { ICitizenProfileDto } from "@/services/api/models/CitizenProfileDto.model";
import { ICompanyProfileDto } from "@/services/api/models/CompanyProfileDto.model";
import { ILawyerProfileDto } from "@/services/api/models/LawyerProfileDto.model";
import { ILegalPersonProfileDto } from "@/services/api/models/LegalPersonProfileDto.model";
import { ProfileDto } from "@/services/api/models/ProfileDto.model";
import { ISaveCompanyProfileCommand } from "@/services/api/models/SaveCompanyProfileCommand.model";
import { ISaveLawyerProfileCommand } from "@/services/api/models/SaveLawyerProfileCommand.model";
import { IProfileQuery } from "@/services/api/models/ProfileQuery.model";
import { IVaccinationProfileQuery } from "@/services/api/models/VaccinationProfileQuery.model";
import { ISelectProfileLoginQuery } from "@/services/api/models/SelectProfileLoginQuery.model";
import { ISaveProfileLawyerSignatureCommand } from "../models/SaveProfileLawyerSignatureCommand.model";
import { IDeleteProfileLawyerSignatureCommand } from "../models/DeleteProfileLawyerSignatureCommand.model";
import { ISaveEmployeeProfileCommand } from "@/services/api/models/SaveEmployeeProfileCommand.model";
import { IEmployeeProfileDto } from "@/services/api/models/EmployeeProfileDto.model";
import { ISaveProfileEmployeeBelongingCommand } from "../models/SaveProfileEmployeeBelongingCommand.model";

export interface IAdditionalProfileDto<T> {
  hasProfile: boolean;
  profileData: T;
}

class ProfileApi extends Api {
  constructor(config: AxiosRequestConfig) {
    super(config);

    this.profile = this.profile.bind(this);
    this.getCitizenProfile = this.getCitizenProfile.bind(this);
    this.setCitizenProfile = this.setCitizenProfile.bind(this);
    this.deleteCitizenProfile = this.deleteCitizenProfile.bind(this);
    this.getCompanyProfile = this.getCompanyProfile.bind(this);
    this.setCompanyProfile = this.setCompanyProfile.bind(this);
    this.deleteCompanyProfile = this.deleteCompanyProfile.bind(this);
    this.getLawyerProfile = this.getLawyerProfile.bind(this);
    this.setLawyerProfile = this.setLawyerProfile.bind(this);
    this.deleteLawyerProfile = this.deleteLawyerProfile.bind(this);
    this.getLegalPersonProfile = this.getLegalPersonProfile.bind(this);
    this.vaccination = this.vaccination.bind(this);
    this.setEmployeeProfile = this.setEmployeeProfile.bind(this);
    this.getEmployeeProfile = this.getEmployeeProfile.bind(this);
    this.deleteEmployeeProfile = this.deleteEmployeeProfile.bind(this);
  }

  public getAdditionalProfiles(): Promise<any> {
    return this.get("api/profile/additional").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public setSelectProfile(data: ISelectProfileLoginQuery): Promise<any> {
    let url = `api/profile/select-login/${data.profile}`;
    if (data.optionId !== "") {
      url = `${url}/${data.optionId}`;
    }
    return this.get(url).then((result: AxiosResponse<any>) => result.data);
  }
  public getCitizenProfile(): Promise<
    IAdditionalProfileDto<ICitizenProfileDto>
  > {
    return this.get("api/profile/citizen").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public setCitizenProfile(): Promise<any> {
    return this.post("api/profile/citizen").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public deleteCitizenProfile(): Promise<any> {
    return this.delete("api/profile/citizen").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public getCompanyProfile(): Promise<
    IAdditionalProfileDto<ICompanyProfileDto>
  > {
    return this.get("api/profile/company").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public setCompanyProfile(data: ISaveCompanyProfileCommand): Promise<any> {
    return this.post("api/profile/company", data).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public deleteCompanyProfile(): Promise<any> {
    return this.delete("api/profile/company").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public getLawyerProfile(): Promise<IAdditionalProfileDto<ILawyerProfileDto>> {
    return this.get("api/profile/lawyer").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public setLawyerProfile(data: ISaveLawyerProfileCommand): Promise<any> {
    return this.post("api/profile/lawyer", data).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public deleteLawyerProfile(): Promise<any> {
    return this.delete("api/profile/lawyer").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public getLegalPersonProfile(): Promise<
    IAdditionalProfileDto<ILegalPersonProfileDto>
  > {
    return this.get("api/profile/legal-person").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public setLegalPersonProfile(): Promise<any> {
    return this.post("api/profile/legal-person").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public deleteLegalPersonProfile(): Promise<any> {
    return this.delete("api/profile/legal-person").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public profile(data: IProfileQuery): Promise<ProfileDto> {
    return this.get(`api/profile/${data.subjectId}`).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public vaccination(data: IVaccinationProfileQuery): Promise<any> {
    return this.get(`api/profile/vaccination/${data.subject}/${data.pin}`).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public getLawyerSignatureProfile(): Promise<IAdditionalProfileDto<any>> {
    return this.get("api/profile/lawyer-signature").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public setLawyerSignatureProfile(
    data: ISaveProfileLawyerSignatureCommand
  ): Promise<any> {
    return this.post("api/profile/lawyer-signature", data).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public deleteLawyerSignature(
    data: IDeleteProfileLawyerSignatureCommand
  ): Promise<any> {
    return this.delete(`api/profile/lawyer-signature/${data.id}`).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public setEmployeeProfile(data: ISaveEmployeeProfileCommand): Promise<any> {
    return this.post("api/profile/employee", data).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public getEmployeeProfile(): Promise<
    IAdditionalProfileDto<IEmployeeProfileDto>
  > {
    return this.get("api/profile/employee").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public deleteEmployeeProfile(): Promise<any> {
    return this.delete("api/profile/employee").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public setEmployeeBelongingProfile(
    data: ISaveProfileEmployeeBelongingCommand
  ): Promise<any> {
    return this.post("api/profile/employee-belonging", data).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public getEmployeeBelongingProfile(): Promise<IAdditionalProfileDto<any>> {
    return this.get("api/profile/employee-belonging").then(
      (result: AxiosResponse<any>) => result.data
    );
  }
  public deleteEmployeeBelonging(
    data: IDeleteProfileLawyerSignatureCommand
  ): Promise<any> {
    return this.delete(`api/profile/employee-belonging/${data.id}`).then(
      (result: AxiosResponse<any>) => result.data
    );
  }
}

export const profileApi = new ProfileApi(apiConfig);
